import React from 'react';
import { motion } from 'framer-motion';
import { Music, Users, BarChart3, Sparkles, DollarSign, Zap, Calendar, Star, MessageSquare } from 'lucide-react';
import { Card } from '../UI/Card';
import { useTranslation } from 'react-i18next';

export const NightclubBenefits: React.FC = () => {
  const { t } = useTranslation(['common', 'home']);
  const isBlack = document.documentElement.classList.contains('theme-black');
  
  const benefits = [
    {
      icon: Calendar,
      title: t('home:nightclub.benefits.cards.vipManagement.title'),
      description: t('home:nightclub.benefits.cards.vipManagement.description')
    },
    {
      icon: Users,
      title: t('home:nightclub.benefits.cards.guestList.title'),
      description: t('home:nightclub.benefits.cards.guestList.description')
    },
    {
      icon: BarChart3,
      title: t('home:nightclub.benefits.cards.barAnalytics.title'),
      description: t('home:nightclub.benefits.cards.barAnalytics.description')
    },
    {
      icon: Sparkles,
      title: t('home:nightclub.benefits.cards.socialContent.title'),
      description: t('home:nightclub.benefits.cards.socialContent.description')
    },
    {
      icon: DollarSign,
      title: t('home:nightclub.benefits.cards.massiveSavings.title'),
      description: t('home:nightclub.benefits.cards.massiveSavings.description')
    },
    {
      icon: Zap,
      title: t('home:nightclub.benefits.cards.instantSetup.title'),
      description: t('home:nightclub.benefits.cards.instantSetup.description')
    },
    {
      icon: Star,
      title: t('home:nightclub.benefits.cards.customerLoyalty.title'),
      description: t('home:nightclub.benefits.cards.customerLoyalty.description')
    },
    {
      icon: MessageSquare,
      title: t('home:nightclub.benefits.cards.autoResponder.title'),
      description: t('home:nightclub.benefits.cards.autoResponder.description')
    },
    {
      icon: Music,
      title: t('home:nightclub.benefits.cards.eventManagement.title'),
      description: t('home:nightclub.benefits.cards.eventManagement.description')
    }
  ];
  
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Image (disabled in black mode) */}
      {!isBlack && (
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 dark:opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=1920')`
        }}
      />
      )}
      {/* Background overlay (transparent in black mode) */}
      <div className={`absolute inset-0 ${isBlack ? 'bg-transparent' : 'bg-white/90 dark:bg-gray-900/90'}`} />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4"
          >
            {(() => {
              const title = t('home:nightclub.benefits.title');
              // Trouver "Rockstar Operations" pour le mettre en surbrillance
              const match = title.match(/(Rockstar Operations|R.*?o.*?c.*?k.*?s.*?t.*?a.*?r.*?O.*?p.*?e.*?r.*?a.*?t.*?i.*?o.*?n.*?s)/i);
              if (match) {
                return (
                  <>
                    <span className="text-purple-600 dark:text-purple-400">{match[1]}</span>{' '}
                    {title.replace(match[1], '').trim()}
                  </>
                );
              }
              return title;
            })()}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {t('home:nightclub.benefits.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card hover className="h-full">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <benefit.icon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-justify">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

